import { isEmpty, omit, some } from 'es-toolkit/compat';

import { useRowSelectionContextOptional } from 'components/atoms/RowSelectionContext';
import { ResultsMode } from 'interfaces/experimentResults';
import { Permission } from 'interfaces/permissionOption';
import { Study } from 'interfaces/study';
import { useCasesParams } from 'utils/useCasesParams';
import { manualSelectionFilters, paginationAndSortingParams } from 'utils/useCasesParams/constants';
import { CasesParams } from 'utils/useCasesParams/types';
import { useCurrentLabId } from 'utils/useCurrentLab';
import { useEncodedFilters } from 'utils/useEncodedFilters';
import { usePermissions } from 'utils/usePermissions';

export interface ResultsExportContext {
  study?: Study;
  studyId?: string;
  labId?: string;
  resultsMode?: ResultsMode;
  orchestrationId?: string;
  isSlidesMode: boolean;
  hasSpecificSelections: boolean;
  hasIntermediateResultsSelection: boolean;
  areFiltersAppliedExceptStudy: boolean;
  casesParams: CasesParams;
  casesParamsWithoutPaginationAndSorting: Partial<CasesParams>;
  casesParamsWithoutSelections: Partial<CasesParams>;
  generateEncodedParams: ReturnType<typeof useEncodedFilters>['generateEncodedParams'];
  emptyFiltersExceptStudy: ReturnType<typeof useEncodedFilters>['emptyFiltersExceptStudy'];
  canViewInternalLabels: boolean;
  canViewReviewMenu: boolean;
  canDownloadIntermediateResults: boolean;
  canDownloadCellsTable: boolean;
}

// Membership and permission facts shared by the chooser and every focused form; reads existing hooks only.
export const useResultsExportContext = (study?: Study): ResultsExportContext => {
  const studyId = study?.id;
  const { casesParams } = useCasesParams();
  const { labId } = useCurrentLabId();
  const { generateEncodedParams, areFiltersAppliedExceptStudy, emptyFiltersExceptStudy } = useEncodedFilters();
  const { hasPermission } = usePermissions();
  // Select-all is stored as omit mode with no omitted rows, so it leaves every ID list empty.
  const isSelectAllActive = useRowSelectionContextOptional()?.selectionMode === 'omit';

  const hasSpecificSelections = some(manualSelectionFilters, (filterName) => !isEmpty(casesParams?.[filterName]));

  const casesParamsWithoutPaginationAndSorting = {
    ...omit(casesParams, paginationAndSortingParams),
    studyId,
    filters: { ...casesParams?.filters, studyId },
  };

  return {
    study,
    studyId,
    labId,
    resultsMode: casesParams?.resultsMode,
    orchestrationId: casesParams?.filters?.orchestrationId,
    isSlidesMode: Boolean(casesParams?.slidesMode),
    hasSpecificSelections,
    hasIntermediateResultsSelection: hasSpecificSelections || isSelectAllActive,
    areFiltersAppliedExceptStudy: Boolean(areFiltersAppliedExceptStudy),
    casesParams,
    casesParamsWithoutPaginationAndSorting,
    casesParamsWithoutSelections: omit(casesParamsWithoutPaginationAndSorting, manualSelectionFilters),
    generateEncodedParams,
    emptyFiltersExceptStudy,
    canViewInternalLabels: hasPermission(Permission.ViewInternalSlideLabels),
    canViewReviewMenu: hasPermission(Permission.ViewReviewMenu),
    canDownloadIntermediateResults: hasPermission(Permission.DownloadIntermediateResults),
    canDownloadCellsTable: hasPermission(Permission.DownloadCellsTable),
  };
};
